import React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../lib/config'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'
import type { Listing } from '../types'

type PageResponse<T> = {
  content: T[]
  totalPages: number
  number: number
}

type FormState = {
  title: string
  city: string
  district: string
  price: string
  size: string
  furnished: boolean
  availableFrom: string
  duration: string
  imageUrl: string
  description: string
  contactEmail: string
}

const PAGE_SIZE = 10

const createFormState = (listing?: Listing, fallbackEmail?: string): FormState => ({
  title: listing?.title ?? '',
  city: listing?.city ?? '',
  district: listing?.district ?? '',
  price: listing?.price != null ? String(listing.price) : '',
  size: listing?.size != null ? String(listing.size) : '',
  furnished: listing?.furnished ?? false,
  availableFrom: listing?.availableFrom ? listing.availableFrom.slice(0, 10) : '',
  duration: listing?.duration ?? '',
  imageUrl: listing?.imageUrl ?? listing?.image ?? '',
  description: listing?.description ?? '',
  contactEmail: listing?.contactEmail ?? fallbackEmail ?? ''
})

const formatDate = (value?: string | null) => {
  if (!value) return '–'
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value
    }
    return date.toLocaleDateString('de-DE')
  } catch (err) {
    return value
  }
}

const MyListings: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toastPromise } = useToast()
  const toastMessages = useToastMessages()

  const [listings, setListings] = React.useState<Listing[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Listing | null>(null)
  const [form, setForm] = React.useState<FormState>(() => createFormState(undefined, user?.email))
  const [saving, setSaving] = React.useState(false)
  const [actionId, setActionId] = React.useState<number | null>(null)

  const fetchListings = React.useCallback(async (pageIndex: number) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/wg/listings/me?page=${pageIndex}&size=${PAGE_SIZE}`, {
        credentials: 'include'
      })

      if (response.status === 401 || response.status === 403) {
        setError('Nicht autorisiert, bitte erneut anmelden.')
        setListings([])
        setTotalPages(1)
        return
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = (await response.json()) as PageResponse<Listing>
      const items = data.content ?? []
      const total = Math.max(1, data.totalPages ?? 1)

      setListings(items)
      setTotalPages(total)

      if (pageIndex + 1 > total) {
        setPage(total)
      }
    } catch (err) {
      console.error('Failed to load listings', err)
      setError('Fehler beim Laden deiner Anzeigen.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchListings(page - 1).catch(() => {
      /* handled in fetchListings */
    })
  }, [fetchListings, page])

  React.useEffect(() => {
    if (!dialogOpen && !editing && user?.email) {
      setForm(createFormState(undefined, user.email))
    }
  }, [dialogOpen, editing, user])

  const openEditDialog = (listing: Listing) => {
    setEditing(listing)
    setForm(createFormState(listing, user?.email))
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (saving) return
    setDialogOpen(false)
    setEditing(null)
    setForm(createFormState(undefined, user?.email))
  }

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editing) {
      return
    }

    setSaving(true)
    const payload = {
      title: form.title.trim(),
      city: form.city.trim(),
      district: form.district.trim() || undefined,
      price: Number(form.price),
      size: Number(form.size),
      furnished: form.furnished,
      availableFrom: form.availableFrom ? form.availableFrom : undefined,
      duration: form.duration.trim() || undefined,
      imageUrl: form.imageUrl.trim() || undefined,
      description: form.description.trim() || undefined,
      contactEmail: form.contactEmail.trim()
    }

    const promise = fetch(`${API_BASE_URL}/wg/listings/${editing.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      return response
    })

    toastPromise(promise, {
      loading: toastMessages.saving(),
      success: () => {
        setDialogOpen(false)
        setEditing(null)
        setForm(createFormState(undefined, user?.email))
        fetchListings(page - 1).catch(() => {
          /* handled */
        })
        return toastMessages.listingUpdated()
      },
      error: toastMessages.listingError(),
      onFinally: () => {
        setSaving(false)
      },
    })
  }

  const handleDelete = async (listing: Listing) => {
    const confirm = window.confirm(`Anzeige "${listing.title}" wirklich löschen?`)
    if (!confirm) {
      return
    }

    setActionId(listing.id)
    const promise = fetch(`${API_BASE_URL}/wg/listings/${listing.id}`, {
      method: 'DELETE',
      credentials: 'include'
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      return response
    })

    toastPromise(promise, {
      loading: 'Lösche...',
      success: () => {
        if (listings.length === 1 && page > 1) {
          setPage(prev => Math.max(1, prev - 1))
        } else {
          fetchListings(page - 1).catch(() => {
            /* handled */
          })
        }
        return toastMessages.listingDeleted()
      },
      error: toastMessages.listingDeleteError(),
      onFinally: () => {
        setActionId(null)
      },
    })
  }

  const hasNoListings = !loading && !error && listings.length === 0

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <div>
            <Typography variant="h4" fontWeight={700}>Meine Anzeigen</Typography>
            <Typography variant="body1" color="text.secondary">
              Verwalte deine veröffentlichten WG-Angebote. Du kannst Anzeigen bearbeiten oder löschen.
            </Typography>
          </div>
          <Button variant="contained" onClick={() => navigate('/new')}>
            Neue Anzeige erstellen
          </Button>
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {hasNoListings && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Du hast noch keine Anzeigen.</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Starte jetzt und veröffentliche dein WG-Angebot.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/new')}>
              Anzeige erstellen
            </Button>
          </Paper>
        )}

        {!loading && !error && listings.length > 0 && (
          <Stack spacing={2}>
            {listings.map(item => (
              <Paper key={item.id} sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                    <Typography variant="h6" color="primary">{item.price} € / Monat</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {item.city}{item.district ? ` • ${item.district}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`${item.size} m²`} />
                    <Chip label={item.furnished ? 'Möbliert' : 'Unmöbliert'} color={item.furnished ? 'success' : 'default'} />
                    <Chip label={`Verfügbar ab ${formatDate(item.availableFrom)}`} />
                  </Stack>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {item.description}
                    </Typography>
                  )}
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      Erstellt am {formatDate(item.createdAt)} · Kontakt: {item.contactEmail || '–'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" onClick={() => openEditDialog(item)}>
                        Bearbeiten
                      </Button>
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => handleDelete(item)}
                        disabled={actionId === item.id}
                      >
                        {actionId === item.id ? 'Löschen…' : 'Löschen'}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {!loading && !error && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            <Pagination
              count={totalPages}
              page={Math.min(page, totalPages)}
              onChange={(_e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <form onSubmit={handleSave}>
          <DialogTitle>Anzeige bearbeiten</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Titel" required fullWidth value={form.title} onChange={e => updateForm('title', e.target.value)} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Stadt" required fullWidth value={form.city} onChange={e => updateForm('city', e.target.value)} />
                <TextField label="Stadtteil" fullWidth value={form.district} onChange={e => updateForm('district', e.target.value)} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Preis (€)" type="number" required fullWidth value={form.price} onChange={e => updateForm('price', e.target.value)} />
                <TextField label="Größe (m²)" type="number" required fullWidth value={form.size} onChange={e => updateForm('size', e.target.value)} />
              </Stack>
              <FormControlLabel
                control={<Checkbox checked={form.furnished} onChange={e => updateForm('furnished', e.target.checked)} />}
                label="Möbliert"
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Verfügbar ab"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={form.availableFrom}
                  onChange={e => updateForm('availableFrom', e.target.value)}
                />
                <TextField
                  label="Dauer"
                  fullWidth
                  value={form.duration}
                  onChange={e => updateForm('duration', e.target.value)}
                  placeholder="z. B. 6-12 Monate"
                />
              </Stack>
              <TextField
                label="Bild URL"
                fullWidth
                value={form.imageUrl}
                onChange={e => updateForm('imageUrl', e.target.value)}
                placeholder="https://..."
              />
              <TextField
                label="Kontakt E-Mail"
                type="email"
                required
                fullWidth
                value={form.contactEmail}
                onChange={e => updateForm('contactEmail', e.target.value)}
              />
              <TextField
                label="Beschreibung"
                fullWidth
                multiline
                minRows={4}
                value={form.description}
                onChange={e => updateForm('description', e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={saving}>Abbrechen</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  )
}

export default MyListings

